<template>
    <FormSection :fullHeight="false" :applicationId="inquiry.application">
        <template v-slot:content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <span class="mt-1 big-text">
                    <b>Save Inquiry In Progress...</b>
                </span>
            </section>
            <section class="row p-2" v-if="!isLoading">
                <div class="col-12 offset-lg-1 col-lg-5 row pe-3">
                    <div class="form-group col-12 col-lg-5 pe-lg-2 mb-0">
                        <label for="contact-first-name"> Contact First Name </label>
                        <span class="text-danger">*</span>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="contact-first-name"
                            type="text"
                            placeholder="Enter Contact First Name"
                            v-model="inquiry.contactFirstName"
                            :class="`${!v$.inquiry.contactFirstName.$error ? '' : 'field-invalid'}`"
                            @blur="v$.inquiry.contactFirstName.$touch"
                        />
                        <span v-if="v$.inquiry.contactFirstName.$error" class="field-invalid"
                            >Contact First Name is required.</span
                        >
                    </div>
                    <div class="form-group col-2 pe-lg-2">
                        <label for="contact-middle-initial" v-tooltip="{ title: `Contact's Middle Initial` }">
                            M.I.
                        </label>
                        <input
                            class="form-control"
                            maxlength="1"
                            name="contact-middle-initial"
                            type="text"
                            placeholder="Enter M.I."
                            v-model="inquiry.contactMiddleInitial"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-5">
                        <label for="contact-last-name">
                            Last Name<span v-if="inquiry.application == applicationEnum.AgencyCore">/Facility </span>
                        </label>
                        <span class="text-danger">*</span>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="contact-last-name"
                            type="text"
                            placeholder="Enter Contact Last Name"
                            v-model="inquiry.contactLastName"
                            :class="`${!v$.inquiry.contactLastName.$error ? '' : 'field-invalid'}`"
                            @blur="v$.inquiry.contactLastName.$touch"
                        />
                        <span v-if="v$.inquiry.contactLastName.$error" class="field-invalid"
                            >Contact Last Name is required.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <label for="contact-phone-number"> Contact Phone Number</label>
                        <span class="text-danger">*</span>
                        <div :class="`${!isPhoneNumberInValid ? '' : 'phone-invalid'}`">
                            <PhoneNumber
                                v-if="isHomeCareInquiry"
                                id="contact-phone-number"
                                :enableCountry="true"
                                v-model:value="inquiry.contactPhoneNumbers[0].number"
                                v-model:country="inquiry.contactPhoneNumbers[0].country"
                                v-model:extension="inquiry.contactPhoneNumbers[0].extension"
                                v-model:type="inquiry.contactPhoneNumbers[0].type"
                                v-number-only
                                v-on:blurredInput="v$.inquiry.contactPhoneNumbers[0].number.$touch"
                                :class="`${!v$.inquiry.contactPhoneNumbers[0].number.$error ? '' : 'field-invalid'}`"
                            />
                            <input
                                v-else
                                id="contact-phone-number"
                                type="text"
                                class="form-control phone-number-input"
                                :value="formatPhone(inquiry.contactPhoneNumbers[0].number)"
                                maxlength="14"
                                placeholder="Enter Phone Number"
                                @input="(event) => setContactPhone(event.target.value)"
                                @blur="v$.inquiry.contactPhoneNumbers[0].number.$touch"
                                :class="`${!v$.inquiry.contactPhoneNumbers[0].number.$error ? '' : 'field-invalid'}`"
                                v-number-only
                            />
                        </div>
                        <span v-if="v$.inquiry.contactPhoneNumbers[0].number.$error" class="field-invalid"
                            ><span
                                v-if="
                                    v$.inquiry.contactPhoneNumbers[0].number.$errors[0].$message == 'Value is required'
                                "
                                class="field-invalid"
                                >Contact Phone Number is required.</span
                            >
                            <span v-else class="field-invalid">10 Digit phone is required.</span>
                        </span>
                    </div>
                    <div
                        class="form-group col-12 d-flex flex-column"
                        v-if="hasTestAgency && inquiry.application === applicationEnum.HomeCare && isEdit"
                    >
                        <div class="d-flex">
                            <span class="me-3"> Branch Name</span>
                            <span> {{ inquiry.branchName }}</span>
                        </div>
                        <div class="mt-2 d-flex flex-column">
                            <span class="font-size-3xs fw-normal">
                                Select View Branch History to view or modify the branch associated to this client.
                                Select Add Branch to add a new branch and an associated effective date.
                            </span>
                            <div class="d-flex mt-2">
                                <button
                                    type="button"
                                    class="btn btn-primary btn-md me-2"
                                    @click="handleViewBranchHistory"
                                >
                                    View Branch History
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary btn-md"
                                    v-if="canAddBranches"
                                    @click="handleAddBranch"
                                >
                                    Add Branch
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-12" v-else>
                        <label for="agency-location-id">
                            Branch
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            class="form-select"
                            v-model="inquiry.locationId"
                            @change="selectedBranch(inquiry.locationId)"
                            @blur="v$.inquiry.locationId.$touch"
                            :class="`${!v$.inquiry.locationId.$error ? '' : 'field-invalid'}`"
                        >
                            <option :value="null">Select Branch</option>
                            <option v-for="branch in branches" :value="branch.id" :key="branch.id">
                                {{ branch.name }}
                            </option>
                        </select>
                        <span v-if="v$.inquiry.locationId.$error" class="field-invalid">Branch is required.</span>
                    </div>
                    <div class="form-group col-12">
                        <label for="date" class="d-inline-block"> Inquiry Date </label>
                        <span class="text-danger">*</span>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    name="date"
                                    type="text"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    v-model="inquiry.date"
                                    @blur="v$.inquiry.date.$touch"
                                    :class="`${!v$.inquiry.date.$error ? '' : 'field-invalid'}`"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                                <span v-if="v$.inquiry.date.$error" class="field-invalid">Date is required.</span>
                            </div>
                        </DatePicker>
                    </div>
                </div>
                <div class="col-12 col-lg-5 row ps-3">
                    <div class="form-group col-12 col-lg-5 pe-lg-2">
                        <label for="patient-first-name" v-if="inquiry.application == applicationEnum.AgencyCore">
                            Patient First Name
                        </label>
                        <label for="patient-first-name" v-else> Client First Name </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="patient-first-name"
                            type="text"
                            placeholder="Enter Patient First Name"
                            v-model="inquiry.firstName"
                        />
                    </div>
                    <div class="form-group col-2 pe-lg-2">
                        <label for="patient-middle-initial" v-tooltip="{ title: `Patient's Middle Initial` }">
                            M.I.
                        </label>
                        <input
                            class="form-control"
                            maxlength="1"
                            name="patient-middle-initial"
                            type="text"
                            placeholder="Enter M.I."
                            v-model="inquiry.middleInitial"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-5">
                        <label for="patient-last-name"> Last Name </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="patient-last-name"
                            type="text"
                            placeholder="Enter Patient Last Name"
                            v-model="inquiry.lastName"
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="phone-number" v-if="inquiry.application == applicationEnum.AgencyCore">
                            Patient Phone Number
                        </label>
                        <label for="client-phone-number" v-else> Client Phone Number </label>
                        <PhoneNumber
                            v-if="isHomeCareInquiry"
                            id="client-phone-number"
                            :enableCountry="true"
                            v-model:value="inquiry.phoneNumbers[0].number"
                            v-model:country="inquiry.phoneNumbers[0].country"
                            v-model:extension="inquiry.phoneNumbers[0].extension"
                            v-model:type="inquiry.phoneNumbers[0].type"
                            v-number-only
                        />
                        <input
                            v-else
                            id="client-phone-number"
                            type="text"
                            class="form-control phone-number-input"
                            :value="formatPhone(inquiry.phoneNumbers[0].number)"
                            maxlength="14"
                            placeholder="Enter Phone Number"
                            @input="(event) => setClientPhone(event.target.value)"
                            v-number-only
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="inquiry-comments"> Comments </label>
                        <CommentInput
                            input-name="inquiry-comments"
                            placeholder="Enter Comments"
                            :result="inquiry.comment"
                            v-on:resultUpdatedAppended="resultUpdatedAppended"
                            v-on:resultUpdated="resultUpdated"
                            :application="inquiry.application"
                        />
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<style src="../InquiryForm/InquiryForm.scss"></style>
<script lang="ts" src="./InquiryFormHomecareHomehealth.ts"></script>
